"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { platformSummary as data } from "@/lib/data/about";

const engineColors = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
] as const;

export function PlatformSummary() {
  return (
    <Section id="platform" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[var(--color-crimson)] opacity-20 blur-[140px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-crimson)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <Reveal>
            <div className="glass rounded-2xl p-6">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Nine Proprietary AI Engines</div>
              <div className="mt-5 space-y-3">
                {data.engines.map((engine, i) => {
                  const accent = engineColors[i % engineColors.length]!;
                  return (
                    <div
                      key={engine.code}
                      className="flex items-start gap-4 p-3 rounded-xl hover:bg-[rgb(var(--border)/0.04)] transition-colors"
                    >
                      <span
                        className="grid place-items-center h-10 w-10 rounded-lg shrink-0 font-bold text-[10px] tracking-wider"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          color: accent,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        {engine.code}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{engine.name}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{engine.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={120}>
              <div className="glass rounded-2xl p-6">
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">50+ Platform Coverage</div>
                <div className="mt-4 space-y-2.5">
                  {data.coverage.map((c) => (
                    <div key={c.label} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-[rgb(var(--fg-muted))]">{c.label}</span>
                      <span className="font-semibold text-[var(--color-cyan)]">{c.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="glass rounded-2xl p-6">
                <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Measurable Outcomes</div>
                <div className="mt-4 space-y-3">
                  {data.outcomes.map((o) => (
                    <div key={o.label}>
                      <div className="text-xs text-[rgb(var(--fg-muted))]">{o.label}</div>
                      <div className="text-2xl font-bold text-[var(--color-crimson)] mt-0.5">{o.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={300}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
